import { escapeNullBytesDeep, unescapeNullBytesDeep } from './null-bytes'

/**
 * Encoder for JSONB columns - stringifies and escapes null bytes
 */
export function encodeJsonb(value: unknown): string {
  const escaped = escapeNullBytesDeep(value)
  return JSON.stringify(escaped)
}

/**
 * SQLite JSON columns store JSON text, including quoted string values.
 * Parse every string; unlike PostgreSQL JSONB, the driver has not decoded it.
 */
export function decodeJsonText(value: unknown): unknown {
  return unescapeNullBytesDeep(
    typeof value === 'string' ? JSON.parse(value) as unknown : value,
  )
}

/**
 * Decoder for JSONB columns - parses and unescapes null bytes
 *
 * Note: PGlite and most PostgreSQL drivers automatically parse JSONB columns,
 * returning JavaScript objects/arrays/primitives rather than JSON strings.
 * We only need to JSON.parse if the driver returns a raw JSON string
 * (indicated by starting with { or [ or ").
 */
// oxlint-disable-next-line typescript-eslint/no-unnecessary-type-parameters -- callers use T to recover typed JSON payloads
export function decodeJsonb<T>(value: unknown): T {
  if (typeof value === 'string') {
    // Only parse if it looks like a JSON structure or quoted string
    // PGlite typically returns already-parsed values, but some drivers
    // return raw JSON strings
    const trimmed = value.trim()
    if (
      trimmed.startsWith('{') ||
      trimmed.startsWith('[') ||
      trimmed.startsWith('"')
    ) {
      try {
        // oxlint-disable-next-line typescript-eslint/no-unsafe-assignment
        const parsed = JSON.parse(value)
        return unescapeNullBytesDeep(parsed) as T
      } catch {
        // If JSON parsing fails, it's a plain string value
        return unescapeNullBytesDeep(value) as T
      }
    }
    // Plain string value (not JSON-encoded) - just unescape null bytes
    return unescapeNullBytesDeep(value) as T
  }
  // Already parsed by the driver
  return unescapeNullBytesDeep(value) as T
}

/**
 * Decoder for timestamp columns - converts to Date
 */
export function decodeTimestamp(value: unknown): Date {
  if (value instanceof Date) {
    return value
  }
  if (typeof value === 'string' || typeof value === 'number') {
    return new Date(value)
  }
  throw new Error(`Cannot decode timestamp from: ${typeof value}`)
}

/**
 * Decoder for bigint columns - converts string to bigint
 */
export function decodeBigint(value: unknown): bigint {
  if (typeof value === 'bigint') {
    return value
  }
  if (typeof value === 'string' || typeof value === 'number') {
    return BigInt(value)
  }
  throw new Error(`Cannot decode bigint from: ${typeof value}`)
}
